import { CurrentUser } from 'customTypes'
import pluralize from 'pluralize'
import { getObject, LocalStorageKeys } from 'utils/storage'
import { CoachesCoachingKind, CoachesCoachingKindDef } from 'types'

type CoachDescriptorVariant = 'coachTitle' | 'shortCoachTitle' | 'hubTitle' | 'serviceTitle'
type CoachingKindDescriptor = Pick<
  CoachesCoachingKindDef,
  'coachTitle' | 'shortCoachTitle' | 'hubTitle' | 'serviceTitle'
>
type VideoCallsFlowKind = 'nutrition' | 'stress'
type DefaultCoachingKind = Pick<
  CoachesCoachingKindDef,
  | 'key'
  | 'coachTitle'
  | 'shortCoachTitle'
  | 'hubTitle'
  | 'clinical'
  | 'insuranceReimbursable'
  | 'serviceTitle'
>

const DEFAULT_NUTRITION_COACHING_KIND: DefaultCoachingKind = {
  key: CoachesCoachingKind.Mnt,
  coachTitle: 'Nutritionist',
  shortCoachTitle: 'Nutritionist',
  hubTitle: 'Nutritionist Hub',
  clinical: true,
  insuranceReimbursable: true,
  serviceTitle: 'Nutrition'
}

const DEFAULT_STRESS_COACHING_KIND: DefaultCoachingKind = {
  key: CoachesCoachingKind.StressManagement,
  coachTitle: 'Stress Management Coach',
  shortCoachTitle: 'Coach',
  hubTitle: 'Coach Hub',
  clinical: true,
  insuranceReimbursable: true,
  serviceTitle: 'Stress Management'
}

interface CoachDescriptorOptions {
  variant?: CoachDescriptorVariant
  capitalized?: boolean
  plural?: boolean
}

class CoachDescriptor {
  private coachingKind?: CoachingKindDescriptor | null
  private options: CoachDescriptorOptions

  constructor(coachingKind?: CoachingKindDescriptor | null, options: CoachDescriptorOptions = {}) {
    this.coachingKind = coachingKind
    this.options = options
  }

  get shortTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'shortCoachTitle' })
  }

  get hubTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'hubTitle' })
  }

  get serviceTitle(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, variant: 'serviceTitle' })
  }

  get capitalized(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, capitalized: true })
  }

  get plural(): CoachDescriptor {
    return new CoachDescriptor(this.coachingKind, { ...this.options, plural: true })
  }

  toString(): string {
    const variant = this.options.variant ?? 'coachTitle'
    let descriptor = this.coachingKind ? this.coachingKind[variant] : ''

    if (!this.options.capitalized) {
      descriptor = descriptor.toLowerCase()
    }
    if (this.options.plural) {
      descriptor = pluralize(descriptor)
    }

    return descriptor
  }
}

export const getCoachingKindDescriptor = (
  coachingKind?: CoachingKindDescriptor | null,
  options: CoachDescriptorOptions = {}
) => {
  return new CoachDescriptor(coachingKind, options)
}

const getStoredVideoCallsFlowKind = (): VideoCallsFlowKind => {
  const flowKind = getObject<{ flowKind?: string }>(
    LocalStorageKeys.VideoCallFlowCustomizationParams
  )?.flowKind

  return flowKind === 'stress' ? 'stress' : 'nutrition'
}

export const getDefaultCoachingKind = (
  flowKind: string = getStoredVideoCallsFlowKind()
): DefaultCoachingKind => {
  return flowKind === 'stress' ? DEFAULT_STRESS_COACHING_KIND : DEFAULT_NUTRITION_COACHING_KIND
}

export const getCoachDescriptor = (
  currentUser?: CurrentUser | null,
  options: CoachDescriptorOptions = {}
) => {
  const coachingKind = currentUser?.primaryCoachingKind ?? getDefaultCoachingKind()

  return getCoachingKindDescriptor(coachingKind, options)
}
